import React from 'react';
import { styled } from '@compiled/react';
import { parseISO } from 'date-fns';
import { Text } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';
import { ScreenReaderText } from '@atlassian/jira-accessibility/src/common/ui/screenreader-text/index.tsx';
import { type IntlShape, useIntl } from '@atlassian/jira-intl';
import { formatWithLocale } from '@atlassian/jira-platform-utils-date-fns/src/main';
import { useTenantContext } from '@atlassian/jira-tenant-context-controller/src/components/tenant-context/index.tsx';
import type { Sprint } from '../../common/types';
import messages from './messages';

type DetailsProps = {
	sprint: Sprint | null;
};

type DateProps = {
	startDate: string;
	endDate: string;
	locale: string;
};

type GoalProps = {
	goal: string;
};

const SprintDate = ({ startDate, endDate, locale }: DateProps) => {
	const { formatMessage } = useIntl();

	if (!startDate || !endDate) {
		return null;
	}
	const start = formatWithLocale(parseISO(startDate), 'PPP', locale);
	const end = formatWithLocale(parseISO(endDate), 'PPP', locale);

	return (
		<div>
			<span aria-hidden="true">
				<Text weight="bold">{formatMessage(messages.sprintDate)}</Text> - {`${start} - ${end}`}
			</span>
			<ScreenReaderText>
				{`${formatMessage(messages.sprintDate)} - ${formatMessage(messages.screenReaderSprintDate, {
					startDate: start,
					endDate: end,
				})}`}
			</ScreenReaderText>
		</div>
	);
};

const SprintGoal = ({ goal }: GoalProps) => {
	const { formatMessage } = useIntl();

	if (!goal) {
		return null;
	}

	return (
		<div>
			<Text weight="bold">{formatMessage(messages.sprintGoal)}</Text> - {goal}
		</div>
	);
};

export const SprintDetails = ({ sprint }: DetailsProps) => {
	const tenantContext = useTenantContext();

	if (!sprint) {
		return null;
	}

	return (
		<SprintDetailsContainer>
			<SprintDate
				startDate={sprint.startDate}
				endDate={sprint.endDate}
				locale={tenantContext.locale}
			/>
			<SprintGoal goal={sprint.goal} />
		</SprintDetailsContainer>
	);
};

type DetailsPropsLegacy = {
	intl: IntlShape;
	sprint: Sprint | null;
};

type DatePropsLegacy = {
	intl: IntlShape;
	startDate: string;
	endDate: string;
	locale: string;
};

type GoalPropsLegacy = {
	intl: IntlShape;
	goal: string;
};

/* @deprecated - use SprintDate instead which removes intl prop and uses useIntl hook */
const SprintDateLegacy = ({ startDate, endDate, locale, intl }: DatePropsLegacy) => {
	if (!startDate || !endDate) {
		return null;
	}
	const start = formatWithLocale(parseISO(startDate), 'PPP', locale);
	const end = formatWithLocale(parseISO(endDate), 'PPP', locale);

	return (
		<div>
			<span aria-hidden="true">
				<Text as="strong" color="inherit">
					{intl.formatMessage(messages.sprintDate)}
				</Text>{' '}
				- {`${start} - ${end}`}
			</span>
			<ScreenReaderText>
				{`${intl.formatMessage(messages.sprintDate)} - ${intl.formatMessage(
					messages.screenReaderSprintDate,
					{
						startDate: start,
						endDate: end,
					},
				)}`}
			</ScreenReaderText>
		</div>
	);
};

/* @deprecated - use SprintGoal instead which removes intl prop and uses useIntl hook */
const SprintGoalLegacy = ({ goal, intl }: GoalPropsLegacy) => {
	if (!goal) {
		return null;
	}
	return (
		<div>
			<Text as="strong" color="inherit">
				{intl.formatMessage(messages.sprintGoal)}
			</Text>{' '}
			- {goal}
		</div>
	);
};

/* @deprecated - use SprintDetails instead which removes intl prop and uses useIntl hook */
export const SprintDetailsLegacy = ({ sprint, intl }: DetailsPropsLegacy) => {
	const tenantContext = useTenantContext();

	if (!sprint) {
		return null;
	}

	return (
		<SprintDetailsContainer>
			<SprintDateLegacy
				intl={intl}
				startDate={sprint.startDate}
				endDate={sprint.endDate}
				locale={tenantContext.locale}
			/>
			<SprintGoalLegacy intl={intl} goal={sprint.goal} />
		</SprintDetailsContainer>
	);
};

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const SprintDetailsContainer = styled.div({
	display: 'flex',
	flexDirection: 'column',
	marginTop: `${token('space.400', '32px')}`,
	gap: `${token('space.100', '8px')}`,
});
